import React, { useState, useEffect, useMemo } from 'react';
import { DIAGNOSIS_OPTIONS, CYP_DIAGNOSIS_ADDITIONS, migrateDiagnosisValue } from './AssessmentTab';
import '../AssessmentForm.css';

/**
 * TreatmentGoalsProblemDescriptorTriageFollowUpTab
 *
 * Handles Goals and Problem Descriptor section for Triage Follow Up reports
 * Maps to TreatmentGoals.Triage, TriageDetails.Report, and TriageInjuryDetails.Report tables
 *
 * Report types: 2003 (Triage Follow Up)
 * Section: psych-triage-followup-treatment-goals-problem-descriptor
 * TabPerInjury: TRUE (per-injury tab)
 */

const TreatmentGoalsProblemDescriptorTriageFollowUpTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [tg1, setTG1] = useState('');
  const [tg1Achieved, setTG1Achieved] = useState(0);
  const [tg2, setTG2] = useState('');
  const [tg2Achieved, setTG2Achieved] = useState(0);
  const [motivationToChange, setMotivationToChange] = useState('');
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState('');
  const [primaryDiagnosisOther, setPrimaryDiagnosisOther] = useState('');
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState('');
  const [secondaryDiagnosisOther, setSecondaryDiagnosisOther] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  const diagnosisOptions = useMemo(() => {
    if (patientInfo?.isCYPApplicable) {
      const combined = [...DIAGNOSIS_OPTIONS, ...CYP_DIAGNOSIS_ADDITIONS];
      return combined.sort((a, b) => a.value.localeCompare(b.value));
    }
    return DIAGNOSIS_OPTIONS;
  }, [patientInfo]);

  useEffect(() => {
    loadTreatmentGoalsProblemDescriptorData();
  }, [reportId]);

  const loadTreatmentGoalsProblemDescriptorData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-treatment-goals-problem-descriptor`);

      if (response.ok) {
        const data = await response.json();

        setTG1(data.tG1 || '');
        setTG1Achieved(data.tG1Achieved !== null ? data.tG1Achieved : 0);
        setTG2(data.tG2 || '');
        setTG2Achieved(data.tG2Achieved !== null ? data.tG2Achieved : 0);
        setMotivationToChange(data.motivationToChange || '');
        const primary = migrateDiagnosisValue(data.primaryDiagnosis || '');
        const secondary = migrateDiagnosisValue(data.secondaryDiagnosis || '');
        setPrimaryDiagnosis(primary);
        setPrimaryDiagnosisOther(data.primaryDiagnosisOther || '');
        // If backend has duplicate diagnoses, clear secondary
        setSecondaryDiagnosis(primary && primary === secondary ? '' : secondary);
        setSecondaryDiagnosisOther(primary && primary === secondary ? '' : (data.secondaryDiagnosisOther || ''));

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.tG1) initialSavingStates['TG1'] = 'saved';
        if (data.tG1Achieved !== null) initialSavingStates['TG1Achieved'] = 'saved';
        if (data.tG2) initialSavingStates['TG2'] = 'saved';
        if (data.tG2Achieved !== null) initialSavingStates['TG2Achieved'] = 'saved';
        if (data.motivationToChange) initialSavingStates['MotivationToChange'] = 'saved';
        if (data.primaryDiagnosis) initialSavingStates['PrimaryDiagnosis'] = 'saved';
        if (data.primaryDiagnosisOther) initialSavingStates['PrimaryDiagnosisOther'] = 'saved';
        if (data.secondaryDiagnosis) initialSavingStates['SecondaryDiagnosis'] = 'saved';
        if (data.secondaryDiagnosisOther) initialSavingStates['SecondaryDiagnosisOther'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading treatment goals problem descriptor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-treatment-goals-problem-descriptor/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'TG1': tg1,
      'TG1Achieved': tg1Achieved,
      'TG2': tg2,
      'TG2Achieved': tg2Achieved,
      'MotivationToChange': motivationToChange,
      'PrimaryDiagnosis': primaryDiagnosis,
      'PrimaryDiagnosisOther': primaryDiagnosisOther,
      'SecondaryDiagnosis': secondaryDiagnosis,
      'SecondaryDiagnosisOther': secondaryDiagnosisOther
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined &&
      (typeof fieldValue === 'number' ? true : fieldValue !== '');

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);

    // If primary changes to match current secondary, clear secondary
    if (fieldName === 'PrimaryDiagnosis' && value && value === secondaryDiagnosis) {
      setSecondaryDiagnosis('');
      setSecondaryDiagnosisOther('');
      saveField('SecondaryDiagnosis', '');
    }
  };

  const handleSliderChange = (fieldName, value, setter) => {
    const numValue = parseInt(value, 10);
    setter(numValue);
    saveField(fieldName, numValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading goals and problem descriptor...</p>
      </div>
    );
  }

  // Check if primary and secondary diagnosis are the same (validation warning)
  const hasDuplicateDiagnosis = primaryDiagnosis && secondaryDiagnosis &&
    primaryDiagnosis === secondaryDiagnosis && primaryDiagnosis !== '';

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Goals and Problem Descriptor</h3>

        <p>Goals should be <strong>SMART</strong> (specific, measurable, achievable, realistic and time limited)</p>

        <div className="form-group">
          <label htmlFor="tg1">
            Treatment goal 1:
            {getSaveIndicator('TG1')}
          </label>
          <textarea
            id="tg1"
            value={tg1}
            onChange={(e) => handleFieldChange('TG1', e.target.value, setTG1)}
            onBlur={() => handleFieldBlur('TG1', tg1)}
            rows="3"
            style={{ width: '100%' }}
            placeholder="Enter the first SMART treatment goal for the patient"
          />
        </div>

        <div className="form-group">
          <label htmlFor="tg1Achieved">
            % Achieved:
            {getSaveIndicator('TG1Achieved')}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <input
              type="range"
              id="tg1Achieved"
              min="0"
              max="100"
              value={tg1Achieved}
              onChange={(e) => handleSliderChange('TG1Achieved', e.target.value, setTG1Achieved)}
              style={{ flex: 1, maxWidth: '400px' }}
              placeholder="Enter the second SMART treatment goal for the patient"
            />
            <span style={{ minWidth: '60px', fontWeight: 'bold' }}>{tg1Achieved}%</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="tg2">
            Treatment goal 2:
            {getSaveIndicator('TG2')}
          </label>
          <textarea
            id="tg2"
            value={tg2}
            onChange={(e) => handleFieldChange('TG2', e.target.value, setTG2)}
            onBlur={() => handleFieldBlur('TG2', tg2)}
            rows="3"
            style={{ width: '100%' }}
            />
        </div>

        <div className="form-group">
          <label htmlFor="tg2Achieved">
            % Achieved:
            {getSaveIndicator('TG2Achieved')}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <input
              type="range"
              id="tg2Achieved"
              min="0"
              max="100"
              value={tg2Achieved}
              onChange={(e) => handleSliderChange('TG2Achieved', e.target.value, setTG2Achieved)}
              style={{ flex: 1, maxWidth: '400px' }}
            />
            <span style={{ minWidth: '60px', fontWeight: 'bold' }}>{tg2Achieved}%</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="motivationToChange">
            Motivation to change including any identified barriers:
            {getSaveIndicator('MotivationToChange')}
          </label>
          <textarea
            id="motivationToChange"
            value={motivationToChange}
            onChange={(e) => handleFieldChange('MotivationToChange', e.target.value, setMotivationToChange)}
            onBlur={() => handleFieldBlur('MotivationToChange', motivationToChange)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        {hasDuplicateDiagnosis && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px'
          }}>
            <strong>⚠ Warning:</strong> Primary and Secondary Diagnosis should not be the same.
          </div>
        )}

        <div className="form-group">
          <label htmlFor="primaryDiagnosis">
            Primary Diagnosis:
            {getSaveIndicator('PrimaryDiagnosis')}
          </label>
          <select
            id="primaryDiagnosis"
            value={primaryDiagnosis}
            onChange={(e) => handleSelectChange('PrimaryDiagnosis', e.target.value, setPrimaryDiagnosis)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {diagnosisOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {primaryDiagnosis === 'Other' && (
          <div className="form-group">
            <label htmlFor="primaryDiagnosisOther">
              Please state:
              {getSaveIndicator('PrimaryDiagnosisOther')}
            </label>
            <input
              type="text"
              id="primaryDiagnosisOther"
              value={primaryDiagnosisOther}
              onChange={(e) => handleFieldChange('PrimaryDiagnosisOther', e.target.value, setPrimaryDiagnosisOther)}
              onBlur={() => handleFieldBlur('PrimaryDiagnosisOther', primaryDiagnosisOther)}
              style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="secondaryDiagnosis">
            Secondary Diagnosis:
            {getSaveIndicator('SecondaryDiagnosis')}
          </label>
          <select
            id="secondaryDiagnosis"
            value={secondaryDiagnosis}
            onChange={(e) => handleSelectChange('SecondaryDiagnosis', e.target.value, setSecondaryDiagnosis)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {diagnosisOptions
              .filter(option => !primaryDiagnosis || option.value !== primaryDiagnosis)
              .map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {secondaryDiagnosis === 'Other' && (
          <div className="form-group">
            <label htmlFor="secondaryDiagnosisOther">
              Please state:
              {getSaveIndicator('SecondaryDiagnosisOther')}
            </label>
            <input
              type="text"
              id="secondaryDiagnosisOther"
              value={secondaryDiagnosisOther}
              onChange={(e) => handleFieldChange('SecondaryDiagnosisOther', e.target.value, setSecondaryDiagnosisOther)}
              onBlur={() => handleFieldBlur('SecondaryDiagnosisOther', secondaryDiagnosisOther)}
              style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
            />
          </div>
        )}
      </div>
      </fieldset>

    </div>
  );
};

export default TreatmentGoalsProblemDescriptorTriageFollowUpTab;
